// Acceptable Use Policy for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "scope", title: "Scope",
    body: (<>
      <p>This Acceptable Use Policy (&quot;AUP&quot;) applies to everyone who accesses CareerOS-AI — free users, paid subscribers, admins, and any third-party integrations acting on your behalf. It complements our <Link to="/terms">Terms and Conditions</Link>.</p>
    </>) },
  { n: 2, id: "prohibited-conduct", title: "Prohibited conduct",
    body: (<>
      <p>You must not:</p>
      {bullets([
        "Break the law — CareerOS-AI is not a shield for illegal activity.",
        "Impersonate any real person, employer, or organization.",
        "Fabricate credentials, employment history, education, or certifications you do not hold.",
        "Post or upload content that is defamatory, harassing, hateful, threatening, or targets protected classes.",
        "Post or upload sexually explicit content or content that exploits minors.",
        "Upload malware, viruses, or any code intended to harm CareerOS-AI or its users.",
        "Attempt to access accounts, data, or systems you are not authorized to access.",
        "Interfere with the Service — attempted DDoS, load testing, brute force, or resource exhaustion.",
        "Scrape, mirror, or bulk-download CareerOS content or other users' data.",
        "Reverse engineer, decompile, or extract our prompts, models, or datasets.",
        "Use CareerOS-AI to train competing AI systems.",
        "Bypass rate limits, quotas, plan restrictions, or paywalls.",
        "Resell, sublicense, or provide the Service to third parties without written permission.",
        "Use the Service to generate spam, phishing, unsolicited outreach, or job-application flooding at a scale that harms recipients or platforms.",
      ])}
    </>) },
  { n: 3, id: "content-standards", title: "Content standards",
    body: (<>
      <p>Any content you upload — resumes, portfolios, cover letters, chats, portfolio media — must:</p>
      {bullets([
        "Be truthful and reflect your own experience.",
        "Not infringe intellectual-property or privacy rights of others.",
        "Not include confidential information you are contractually or legally forbidden to share.",
        "Not contain the personal data of others without their consent.",
      ])}
    </>) },
  { n: 4, id: "ai-specific", title: "AI-specific rules",
    body: (<>
      {bullets([
        "Do not use AI features to generate fraudulent credentials, deepfake profiles, or misrepresent your identity.",
        "Do not attempt to jailbreak the AI to produce prohibited content.",
        "Do not automate CareerOS-AI to submit thousands of applications, messages, or comments elsewhere on your behalf.",
        "Do not use the Service to generate content that could reasonably be used to deceive employers, licensing bodies, or educational institutions.",
      ])}
      <p>See our <Link to="/ai-policy">AI Usage &amp; Responsible AI Policy</Link> for the full framework.</p>
    </>) },
  { n: 5, id: "reporting", title: "Reporting violations",
    body: (<>
      <p>If you see behavior that violates this AUP, report it to <span className="font-mono">support@careeros-ai.com</span> with:</p>
      {bullets([
        "A description of what you saw.",
        "The URL, username, or content in question.",
        "A screenshot if possible.",
      ])}
      <p>We do not disclose reporter identities to the party being reported.</p>
    </>) },
  { n: 6, id: "enforcement", title: "Enforcement",
    body: (<>
      <p>We may, at our sole discretion:</p>
      {bullets([
        "Warn you.",
        "Rate-limit or temporarily restrict specific features.",
        "Remove offending content.",
        "Suspend or terminate your account.",
        "Refuse future service.",
        "Notify law enforcement where legally required.",
      ])}
      <p>For material violations we may act without prior notice. Where we can, we will explain the reason so you can respond.</p>
    </>) },
  { n: 7, id: "appeals", title: "Appeals",
    body: <p>If you believe an enforcement action was in error, reply to the notification email or contact <span className="font-mono">support@careeros-ai.com</span>. A human on our team will review the case.</p> },
  { n: 8, id: "changes", title: "Changes to this policy",
    body: <p>We may update this policy. Material changes are communicated on the website. The &quot;Last Updated&quot; date reflects the current version.</p> },
];

const INTRO = (
  <p>CareerOS-AI is a tool for building honest, sharp careers. This Acceptable Use Policy sets the ground rules that keep the Service safe, useful, and trustworthy for everyone.</p>
);

export default function AcceptableUse() {
  return (
    <LegalLayout
      eyebrow="Legal · Acceptable Use"
      title="Acceptable Use Policy"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/acceptable-use"
    />
  );
}
